import { t } from "@/i18n-js/instance";
import { ModalManager } from "@/react/components/Layout/ModalManagerProvider";
import { useAcceptConnectionForUser } from "@/react/components/ProfileModalV3/Body/ConnectBanner/useAcceptConnectionForUser";
import { useIgnoreConnectionForUser } from "@/react/components/ProfileModalV3/Body/ConnectBanner/useIgnoreConnectionForUser";
import type { CommunityMember } from "@/react/types/CommunityMember";
import { Icon } from "../../../Icon";
import { Button } from "../../../uikit/Button";
import { Typography } from "../../../uikit/Typography";
import { AcceptConnectionMomentumModal } from "./AcceptConnectionMomentumModal";

interface IgnoreAcceptProps {
  connectionId?: string;
  recipient: CommunityMember;
  shouldShowOnlyIcon?: boolean;
}

export const IgnoreAccept = ({
  recipient,
  connectionId,
  shouldShowOnlyIcon = false,
}: IgnoreAcceptProps) => {
  const { public_uid: memberPublicUid, name: memberName } = recipient;

  const { acceptConnectionForUser, isAccepting } = useAcceptConnectionForUser({
    memberPublicUid,
    memberName,
    onSuccess: () => {
      if (memberName && connectionId) {
        void ModalManager.show(AcceptConnectionMomentumModal, {
          recipient,
        });
      }
    },
  });

  const { ignoreConnectionForUser, isIgnoring } = useIgnoreConnectionForUser({
    memberPublicUid,
  });

  const handleIgnore = () => {
    if (!connectionId) {
      return;
    }
    ignoreConnectionForUser(connectionId);
  };

  const handleAccept = () => {
    if (!connectionId || !memberPublicUid) {
      return;
    }

    acceptConnectionForUser(connectionId);
  };

  if (!memberName) {
    return null;
  }

  return (
    <div className={shouldShowOnlyIcon ? "flex gap-2" : "flex w-full gap-2"}>
      <Button
        variant="secondary"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          handleIgnore();
        }}
        disabled={isIgnoring || isAccepting}
        aria-label={t("member_directory.ignore")}
        data-testid="ignore-button"
        className={
          shouldShowOnlyIcon
            ? "!h-8 !w-8 !p-0"
            : "!border-primary flex !h-8 flex-1 justify-center !px-4 !font-medium"
        }
      >
        {isIgnoring ? (
          <Icon type="loader" viewBox="0 0 16 16" />
        ) : shouldShowOnlyIcon ? (
          <Icon type="20-close" size={20} />
        ) : (
          <Typography.LabelSm color="text-primary" weight="font-medium">
            {t("member_directory.ignore")}
          </Typography.LabelSm>
        )}
      </Button>
      <Button
        variant="circle"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          handleAccept();
        }}
        disabled={isAccepting || isIgnoring}
        aria-label={t("member_directory.accept")}
        data-testid="accept-button"
        className={
          shouldShowOnlyIcon
            ? "!h-8 !w-8 !p-0"
            : "!border-primary flex !h-8 flex-1 justify-center !px-4 !font-medium"
        }
      >
        {isAccepting ? (
          <Icon type="loader" viewBox="0 0 16 16" />
        ) : shouldShowOnlyIcon ? (
          <Icon type="20-check" size={20} />
        ) : (
          <Typography.LabelSm color="text-primary" weight="font-medium">
            {t("member_directory.accept")}
          </Typography.LabelSm>
        )}
      </Button>
    </div>
  );
};
